import React from "react";
import { Link } from "react-router-dom";
import { styles } from "../../layout";
import Figure from "../components/figure";
import CustomLink from "../components/hoverlink";
import IframeCarousel from "../components/iframe"; // Adjust the path as necessary
import appstore from "./components/app-store/appstore.png";
import downloads from "./components/app-store/downloads.png";
import notion from "./components/app-store/notion.png";

const AppLaunchBlog: React.FC = () => {
  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.header}>How to Get an App on the App Store</h1>

      <p style={styles.paragraph}>
        On March 24, 2025 my startup Forevergreen launched our app on the iOS
        App Store. In this blog I will go over the entire process and my
        experience building and launching an app.
      </p>

      <h2 style={styles.subHeader}>Should I Build an App?</h2>
      <p style={styles.paragraph}>
        Before writing any code this is a question you should ask yourself. As
        easy as some people might make it look, app development is challenging
        and can be a lengthy process depending on features and functionality.
        Looking back, we might have been better off staying web-based and
        attempting to validate our market there before developing an app.
        Nonetheless, it was a valuable experience—so if you have an idea, just
        build it!
      </p>

      <h2 style={styles.subHeader}>Step 1: Design</h2>
      <p style={styles.paragraph}>
        Whether you are coding yourself, working with a dev shop, or hiring a
        software engineer, the first thing you have to do is create your
        designs. The best program to do this is{" "}
        <CustomLink
          href="http://www.figma.com/"
          target="_blank"
          rel="noreferrer"
        >
          Figma
        </CustomLink>
        . They have a super easy and intuitive UI and can make really
        high-quality designs. Here you should map out every screen and the flow
        of the app.
      </p>
      <p style={styles.paragraph}>
        Below I have my first ever design along with some other versions to see
        the progression.
      </p>

      <IframeCarousel
        sources={[
          {
            src: "https://embed.figma.com/design/6eNmABOPoBpJX2UqjiMK1r/Forevergreen?node-id=0-1&embed-host=share",
            title: "Forevergreen Figma Embed v1",
          },
          {
            src: "https://embed.figma.com/design/6hCEUgPQYDfeMDNRZKTEuu/Forevergreen-v2?node-id=0-1&embed-host=share",
            title: "Forevergreen Figma Embed v2",
          },
          {
            src: "https://embed.figma.com/design/SEREamVjLpr5NHKSpGEJzj/Forevergreen-v3?node-id=0-1&embed-host=share",
            title: "Forevergreen Figma Embed v3",
          },
        ]}
        caption="Evolution of Forevergreen's mobile UI in Figma"
      />

      <p style={styles.paragraph}>
        This step is crucial and you should spend a good amount of time working
        through the flow and how the app will function. The better and more
        polished your designs, the easier it is when you begin to code.
      </p>

      <h2 style={styles.subHeader}>Step 2: Plan</h2>
      <p style={styles.paragraph}>
        In software development you might hear the term "
        <CustomLink
          href="https://en.wikipedia.org/wiki/Agile_software_development"
          target="_blank"
          rel="noreferrer"
        >
          Agile Development
        </CustomLink>
        " which uses{" "}
        <CustomLink
          href="https://en.wikipedia.org/wiki/Scrum_(software_development)"
          target="_blank"
          rel="noreferrer"
        >
          Sprints
        </CustomLink>{" "}
        to break up the work. All these methodologies exist to help break down
        complex projects into manageable tasks.
      </p>
      <p style={styles.paragraph}>
        We used Notion to host our task list and any relevant info about the
        project. I posted an image below to give a little idea of the dashboard.
        There are templates in notion specifically for software development but
        there is other software like Trello as well.
      </p>

      <Figure
        imageSrc={notion}
        altText="Notion dashboard"
        caption="Notion dashboard for project management."
        size="large"
      />
      <p style={styles.paragraph}>
        Essentially we broke down each part of the development or features,
        split things up depending on who could do what. Then we slowly checked
        off tasks and added new ones as we got deeper into development. Agile
        development is focused on iterations and building as you go which is
        exactly how we did it.
      </p>
      <h2 style={styles.subHeader}>Step 3: Testing</h2>
      <p style={styles.paragraph}>
        Testing is a super important step and each part of your app should be
        heavily tested by multiple people before release. We mainly used
        TestFlight to test our app and had friends and family test as well as a
        formal beta test program.
      </p>
      <p style={styles.paragraph}>
        For reference, here is our beta test guide that we sent out:{" "}
        <CustomLink
          href="https://fg-beta-test.notion.site/2025"
          target="_blank"
          rel="noreferrer"
        >
          Beta Test Guide
        </CustomLink>
        .
      </p>
      <p style={styles.paragraph}>
        Again make sure you have real users test your app so you can find bugs,
        and make changes depending on their feedback.{" "}
      </p>
      <h2 style={styles.subHeader}>Step 4: Getting App Store Approval</h2>
      <p style={styles.paragraph}>
        This is the final step between you and an app on the app store. Apple
        has a pretty strict review team that will test all aspects of your app
        to make sure it is up to their standards and doesn’t break any rules.
      </p>

      <p style={styles.paragraph}>A few common key things to look out for:</p>

      <ul style={styles.list}>
        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>
              Have full functionality for account and data storage
            </strong>
            <p>Account deletion, forgot password, etc</p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Every screen functions and no bugs</strong>
            <p>
              Just do tests on every screen to make sure there are no bugs,
              Apple will find them, so don’t waste a submisison on something you
              can find.
            </p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Make sure it works on IPad</strong>
            <p>Apple wants the apps to work on all device sizes</p>
          </div>
        </li>

        <li style={styles.listItem}>
          <span style={styles.bulletIcon}>•</span>
          <div>
            <strong>Supplemental information helps</strong>
            <p>
              Add as much info as possible on your submissions so that they can
              focus on testing not figuring out what your app does.
            </p>
          </div>
        </li>
      </ul>
      <Figure
        imageSrc={appstore}
        altText="Downloads chart"
        caption="Downloads chart for app launch."
        size="large"
      />

      <h2 style={styles.subHeader}>Step 5: Launch</h2>
      <p style={styles.paragraph}>
        There are a million ways to do a successful app launch, and to be honest
        I don’t know what is best. We built up our email list and social medias
        prior to our launch. I think our email list was right around 2,000 and
        our social media followers were around 100k on instagram 50k on youtube.
        This allowed us to convert to around 130 downloads in the first two
        days.
      </p>
      <Figure
        imageSrc={downloads}
        altText="Downloads chart"
        caption="Downloads chart for app launch."
        size="large"
      />
      <p style={styles.paragraph}>
        Another launch strategy is to post on app forums to get traction. Our
        app was very niche and had some retention issues but this blog is not
        about how to make an million dollar app, but about how to get one on the
        app store.
      </p>

      <h2 style={styles.subHeader}>Step 6: Iterate and Debug</h2>
      <p style={styles.paragraph}>
        We have found a few errors in the app since launch and have iterated
        some ideas. First we plan to launch in more countries since we have
        gotten demand mostly from Europe. Additionally our payments system
        needed some work so we have optimized that. Building in public and
        talking to your users is all part of the process and a great way to
        build upon your initial MVP.
      </p>

      <p style={styles.paragraph}>
        These are the steps I would take to get an app on the App Store and I
        hope they are helpful if you’re looking to do the same. Good luck in
        your development!
      </p>

      <p>- Jack</p>
      <p style={styles.date}>Published on July 6, 2025</p>

      <Link to="/blogs" style={styles.backLink}>
        Back to Blogs
      </Link>
    </div>
  );
};

export default AppLaunchBlog;
